import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {DialogService} from '../../../../../services/dialog/dialog.service';
import {Paginator} from '../../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../../tools/table-sorter/table-sorter';
import {LandingPage} from '../../../../../models/landing-page/landing-page';
import {LandingPageService} from '../../../../../services/landing-page/landing-page.service';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {FormsModule} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {PaginatorComponent} from '../../../../layout/paginator/paginator.component';
import {TableSorterHeaderDirective} from '../../../../../directives/table-sorter/table-sorter-header.directive';
import {TableSorterDirective} from '../../../../../directives/table-sorter/table-sorter.directive';
import {ConfirmDirective} from '../../../../../directives/confirm/confirm.directive';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {RouterLink} from '@angular/router';
import {IsGrantedDirective} from '../../../../../directives/is-granted/is-granted.directive';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {OfferService} from '../../../../../services/offer/offer.service';
import {debounceTime, forkJoin, Subject} from 'rxjs';
import {Offer} from '../../../../../models/offer';
import {IsGranted} from '../../../../../decorator/is-granted';
import {Product} from '../../../../../models/product';
import {ProductService} from '../../../../../services/product/product.service';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {LangService} from '../../../../../services/language/lang.service';
import {Language} from '../../../../../models/language';
import {CollapserComponent} from '../../../../layout/collapser/collapser.component';
import {CollapserItemComponent} from '../../../../layout/collapser-item/collapser-item.component';
import {PageType} from '../../../../../models/page-type';
import {PageTypeService} from '../../../../../services/page-type/page-type.service';
import {User} from '../../../../../models/user';
import {UserService} from '../../../../../services/user/user.service';
import {ListFilters} from '../../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../../services/list-filter/list-filter.service';
import {LandingPageActivityPeriods} from '../../../../../constants/landing-page-activity';

@Component({
  selector: 'app-landing-page-list',
  imports: [
    FormsModule,
    PaginatorComponent,
    TableSorterHeaderDirective,
    TableSorterDirective,
    ConfirmDirective,
    RouterLink,
    IsGrantedDirective,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    CollapserComponent,
    CollapserItemComponent,
    DatePipe
  ],
  templateUrl: './landing-page-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './landing-page-list.component.scss'
})
@IsGranted(['LANDING_PAGE_VIEW'])
@MetaTitle('Promotion links')
export class LandingPageListComponent implements OnInit {

  landingPages: LandingPage[] = [];
  products: Product[] = [];
  languages: Language[] = [];
  offers: Offer[] = [];
  types: PageType[] = [];
  users: User[] = [];
  activityPeriods = LandingPageActivityPeriods;

  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('id', 'asc');

  filters: ListFilters;

  private nameChange$ = new Subject<void>();

  constructor(
    protected langService: LangService,
    private dialog: DialogService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private landingPageService: LandingPageService,
    private offerService: OfferService,
    private productService: ProductService,
    private pageTypeService: PageTypeService,
    private userService: UserService,
    private listFilterService: ListFilterService,
  ) {
    this.filters = listFilterService.createFilters({
      name: 'string',
      productId: 'number[]',
      lang: 'string[]',
      typeId: 'number[]',
      userId: 'number[]',
      external: 'boolean?',
      activeSince: 'string',
    }, this.paginator, this.tableSorter);
  }

  ngOnInit(): void {
    this.loader.startLoading();

    forkJoin([
      this.offerService.findAll(),
      this.productService.findAll({sort: 'name'}),
      this.pageTypeService.findAll({sort: 'name'}),
      this.langService.getLanguages(),
    ]).subscribe(([offers, products, types, languages]) => {
      this.offers = offers;
      this.products = products;
      this.types = types;
      this.languages = languages;
    })

    this.userService.findAll({sort: 'firstname,lastname'}).subscribe((users: User[]) => {
      this.users = users;
    });

    this.nameChange$.pipe(debounceTime(1000)).subscribe(() => this.filters.onFilterChange());

    this.filters.init(() => this.fetchAll());
  }

  onNameChange(): void {
    this.nameChange$.next();
  }

  fetchAll(): void {
    const params: any = {groups: LandingPage.DETAILS_GROUP + ',site', ...this.filters.toParams()};

    this.loader.startLoading();
    this.landingPageService.findAll(params, this.paginator, this.tableSorter).subscribe((landingPages: LandingPage[]) => {
      this.landingPages = landingPages;
      this.loader.stopLoading();
    });
  }

  getOfferNameById(offerId: number | null): string {
    if (!offerId) {
      return '';
    }
    const offer = this.offers.find((offer: Offer) => offer.id === offerId);

    return offer?.name ?? '';
  }

  duplicate(landingPage: LandingPage): void {
    this.loader.startLoading();
    this.landingPageService.findOneById(landingPage.id!, {groups: LandingPage.DETAILS_GROUP}).subscribe((fullPage: LandingPage) => {
      this.landingPageService.create({
        name: fullPage.name + ' (copy)',
        url: fullPage.url,
        offers: fullPage.offers,
        siteId: fullPage.siteId,
        typeId: fullPage.type?.id ?? null,
        enabled: fullPage.enabled,
        external: fullPage.external,
      }).subscribe({
        next: () => {
          this.loader.stopLoading();
          this.snackbar.success('Promotion link duplicated successfully.');
          this.fetchAll();
        },
        error: () => {
          this.loader.stopLoading();
        }
      });
    });
  }

  delete(landingPage: LandingPage): void {
    if (landingPage.id) {
      this.loader.startLoading();
      this.landingPageService.delete(landingPage.id).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Promotion link has been deleted');
        this.fetchAll();
      });
    }
  }
}
